/**
 * @description Builds the SEO title, description and keywords for person and company
 *              profile pages from the fetched profile, following the templates in the
 *              "Pages for Title" sheet:
 *
 *                person:  {Full Name} - {Title} at {Company} | Radivision
 *
 *              Title and company come from the person's current position, which PERSON_QUERY
 *              already fetches as `experience.edges[].position { title, entity { name } }`.
 *              When there is no usable position the free-text `responsibility` field stands
 *              in, and when that is empty too the title falls back to "{Full Name}" alone, as
 *              the sheet specifies. {Industry} is still missing from the keywords because
 *              PERSON_QUERY does not select it on the experience entity - adding it would mean
 *              a query change and a relay recompile.
 *                company: {Company} - {Industry} Startup Profile | Radivision
 *
 *              Both fall back to "{Name} | Radivision" when the qualifier is absent, and
 *              both are capped at 60 characters so the title is not clipped in the SERP.
 * @filename profile-meta.ts
 */

import { Person } from "@radivision/graphql/lib/ts/graphql/person";

/** Google truncates titles past roughly this width. */
export const MAX_TITLE_LENGTH = 60;

const BRAND_NAME = "Radivision";
const BRAND_SUFFIX = ` | ${BRAND_NAME}`;
const SEPARATOR = " - ";
const COMPANY_QUALIFIER_SUFFIX = " Startup Profile";

/** Shortest qualifier stub worth keeping; below this the qualifier is dropped instead. */
const MIN_QUALIFIER_LENGTH = 12;

const BRAND_FALLBACK_DESCRIPTION =
  "Radivision is the entrepreneurship network for private market and IPO access. Discover startups, watch original series, connect with founders.";
const BRAND_FALLBACK_KEYWORDS = "Radivision, founder profile, company profile, startup profile";

export interface ProfileMeta {
  title: string;
  description: string;
  keywords: string;
}

/** A person's employer and, when known, their job title there, as named in the page title. */
export interface PersonRole {
  title?: string;
  company: string;
}

function clean(value: string | null | undefined): string | undefined {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Truncates by code point rather than by code unit. A plain `slice` can cut an astral
 * character (emoji, some CJK) in half and leave a lone surrogate in the title.
 */
function clip(value: string, max: number): string {
  if (max <= 0) {
    return "";
  }
  const points = Array.from(value);
  return points.length <= max ? value : points.slice(0, max).join("");
}

/** Trims trailing separators and whitespace left behind by a truncation. */
function trimTail(value: string): string {
  return value.replace(/[\s,\-–—]+$/, "");
}

/**
 * True when the value carries something a reader would recognise as a word, rather than
 * only whitespace and punctuation. Deliberately not an alphanumeric test, so that names in
 * non-Latin scripts still qualify.
 */
function hasText(value: string | undefined): boolean {
  return !!value && /[^\s,.\-–—/|:;_'"()\[\]]/.test(value);
}

/** The cleaned value, or undefined when it carries no readable text. */
function meaningful(value: string | null | undefined): string | undefined {
  const cleaned = clean(value);
  return hasText(cleaned) ? cleaned : undefined;
}

/**
 * Assembles "{name} - {qualifier} | Radivision", shortening the qualifier as needed so the
 * whole title fits `MAX_TITLE_LENGTH` with the brand suffix intact. Drops the qualifier
 * entirely when there is no room for a meaningful part of it, and only clips the name when
 * the name alone is already too long.
 */
export function buildProfileTitle(rawName: string, rawQualifier?: string): string {
  const name = meaningful(rawName);
  if (!name) {
    return BRAND_NAME;
  }

  const base = `${name}${BRAND_SUFFIX}`;
  const qualifier = meaningful(rawQualifier);

  if (qualifier) {
    const full = `${name}${SEPARATOR}${qualifier}${BRAND_SUFFIX}`;
    if (full.length <= MAX_TITLE_LENGTH) {
      return full;
    }
    // Room left for the qualifier once the name, separator, ellipsis and suffix are paid for.
    const room = MAX_TITLE_LENGTH - base.length - SEPARATOR.length - 1;
    if (room >= MIN_QUALIFIER_LENGTH) {
      const shortened = trimTail(clip(qualifier, room));
      if (shortened.length > 0) {
        return `${name}${SEPARATOR}${shortened}…${BRAND_SUFFIX}`;
      }
    }
  }

  if (base.length <= MAX_TITLE_LENGTH) {
    return base;
  }
  // The name alone overruns: clip the name rather than the brand suffix.
  const nameRoom = MAX_TITLE_LENGTH - BRAND_SUFFIX.length - 1;
  return `${trimTail(clip(name, nameRoom))}…${BRAND_SUFFIX}`;
}

/**
 * Picks the employer (and title, when known) to name in the page title.
 *
 * First choice is a position from `experience`: the current one, else the one they founded,
 * else the first listed - skipping deleted positions and any missing either the title or
 * the employer. Many profiles carry no `experience` at all but do list their company under
 * `entities` (what the sidebar shows as "Companies"), so that is the second choice, paired
 * with `responsibility` as the title unless `responsibility` already names that company.
 */
export function selectCurrentRole(person: Person | null | undefined): PersonRole | undefined {
  const edges: any[] = (person as any)?.experience?.edges || [];
  const usable = edges
    .map((edge) => edge?.position)
    .filter(
      (position) =>
        position && !position.isDeleted && hasText(clean(position.title)) && hasText(clean(position.entity?.name)),
    );
  if (usable.length > 0) {
    const chosen =
      usable.filter((position) => position.isCurrent)[0] ||
      usable.filter((position) => position.isFounder)[0] ||
      usable[0];
    return { title: clean(chosen.title), company: clean(chosen.entity.name) };
  }

  const company = meaningful(((person as any)?.entities || []).map((entity: any) => entity?.name).filter(Boolean)[0]);
  if (!company) {
    return undefined;
  }
  const responsibility = meaningful((person as any)?.responsibility);
  const alreadyNamesCompany = responsibility && responsibility.toLowerCase().indexOf(company.toLowerCase()) !== -1;
  return { title: alreadyNamesCompany ? undefined : responsibility, company };
}

/** Title, description and keywords for a person profile. */
export function buildPersonMeta(rawFullName: string, responsibility?: string, role?: PersonRole): ProfileMeta {
  const fullName = meaningful(rawFullName);
  if (!fullName) {
    // Nothing nameable to build from; fall back to the brand rather than "Radivision - CEO".
    return { title: BRAND_NAME, description: BRAND_FALLBACK_DESCRIPTION, keywords: BRAND_FALLBACK_KEYWORDS };
  }

  const company = role ? meaningful(role.company) : undefined;
  const jobTitle = role ? meaningful(role.title) : undefined;
  const freeText = meaningful(responsibility);
  // The sheet's template first; then the company alone when no title is known for it,
  // unless the free-text field already tells the fuller story; then the free text; then nothing.
  let qualifier: string | undefined;
  if (jobTitle && company) {
    qualifier = `${jobTitle} at ${company}`;
  } else if (company) {
    const freeTextNamesCompany = freeText && freeText.toLowerCase().indexOf(company.toLowerCase()) !== -1;
    qualifier = freeTextNamesCompany ? freeText : company;
  } else {
    qualifier = freeText;
  }

  const description = qualifier
    ? `${fullName} on Radivision - ${qualifier}. Explore their background, portfolio, pitches, videos, and posts across the startup ecosystem.`
    : `${fullName} on Radivision. Explore their background, portfolio, pitches, videos, and posts across the startup ecosystem.`;

  return {
    title: buildProfileTitle(fullName, qualifier),
    description,
    keywords: [
      fullName,
      company,
      jobTitle || freeText,
      "founder profile",
      "investor profile",
      "startup founder",
      "entrepreneur bio",
      "portfolio",
      "pitch videos",
    ]
      .filter(Boolean)
      .join(", "),
  };
}

/** Title, description and keywords for a company profile. */
export function buildCompanyMeta(rawName: string, industry?: string, tagLine?: string): ProfileMeta {
  const name = meaningful(rawName);
  const sector = meaningful(industry);
  if (!name) {
    return { title: BRAND_NAME, description: BRAND_FALLBACK_DESCRIPTION, keywords: BRAND_FALLBACK_KEYWORDS };
  }

  // The industry is the informative half of the title and "Startup Profile" is boilerplate,
  // so when the sheet's full template overruns, drop the boilerplate before touching the
  // industry. Only once even "{Company} - {Industry}" will not fit does buildProfileTitle
  // shorten the industry itself (and, failing that, drop it).
  let title: string;
  if (sector) {
    const withBoilerplate = `${name}${SEPARATOR}${sector}${COMPANY_QUALIFIER_SUFFIX}${BRAND_SUFFIX}`;
    const withoutBoilerplate = `${name}${SEPARATOR}${sector}${BRAND_SUFFIX}`;
    title =
      withBoilerplate.length <= MAX_TITLE_LENGTH
        ? withBoilerplate
        : withoutBoilerplate.length <= MAX_TITLE_LENGTH
        ? withoutBoilerplate
        : buildProfileTitle(name, sector);
  } else {
    title = buildProfileTitle(name);
  }

  const description =
    clean(tagLine) ||
    `${name} on Radivision - what they build, who is behind it, their pitch, funding context, and the latest updates for investors.`;

  return {
    title,
    description,
    keywords: [
      name,
      sector,
      "startup profile",
      "company profile",
      "funding",
      "founders",
      "investors",
      "pitch deck",
      "private company",
    ]
      .filter(Boolean)
      .join(", "),
  };
}
